// SEO 관련 유틸리티 함수들

/**
 * 페이지별 구조화된 데이터 생성
 */
export const generateStructuredData = (pageType, data = {}) => {
  const baseData = {
    "@context": "https://schema.org",
    "@type": "WebSite",
    name: "올려줌",
    description: "셀프 마케팅 플랫폼",
    url: "https://www.olzoom.com/",
    potentialAction: {
      "@type": "SearchAction",
      target: "https://www.olzoom.com/?tab=addAdvertisement",
      "query-input": "required name=search_term_string",
    },
  };

  switch (pageType) {
    case "dashboard":
      return {
        "@context": "https://schema.org",
        "@type": "WebPage",
        name: "대시보드 - 올려줌 | 광고 현황 및 통계",
        description:
          "올려줌 대시보드에서 광고 현황과 통계를 한눈에 확인하세요. 실시간 광고 성과, 예치금 현황, 키워드 순위 변화를 모니터링할 수 있습니다.",
        url: "https://www.olzoom.com/?tab=dashboard",
        mainEntity: {
          "@type": "WebApplication",
          name: "올려줌 대시보드",
          applicationCategory: "BusinessApplication",
          operatingSystem: "Web Browser",
          description: "광고 현황 및 통계 대시보드",
        },
      };

    case "addAdvertisement":
      return {
        "@context": "https://schema.org",
        "@type": "WebPage",
        name: "광고 신청 - 올려줌 | 네이버 쇼핑/플레이스/앱리뷰 마케팅",
        description:
          "네이버 쇼핑, 플레이스, 앱 리뷰 등 다양한 마케팅 서비스를 신청하세요. 셀프마케팅으로 실제 유저의 검색, 방문, 리뷰, 저장을 확보할 수 있습니다.",
        url: "https://www.olzoom.com/?tab=addAdvertisement",
        mainEntity: {
          "@type": "Service",
          name: "셀프 마케팅 서비스",
          serviceType: "Digital Marketing",
          provider: {
            "@type": "Organization",
            name: "올려줌",
          },
          areaServed: "KR",
          description: "네이버 쇼핑, 플레이스, 앱 리뷰 마케팅 서비스",
        },
      };

    case "myAdvertisement":
      return {
        "@context": "https://schema.org",
        "@type": "WebPage",
        name: "내 광고 관리 - 올려줌 | 광고 현황 및 성과 관리",
        description:
          "신청한 광고의 진행 상황과 성과를 실시간으로 확인하고 관리하세요. 광고 상태, 완료율, 키워드 순위 변화를 한눈에 볼 수 있습니다.",
        url: "https://www.olzoom.com/?tab=myAdvertisement",
        mainEntity: {
          "@type": "WebApplication",
          name: "광고 관리 시스템",
          applicationCategory: "BusinessApplication",
          operatingSystem: "Web Browser",
          description: "광고 현황 및 성과 관리 시스템",
        },
      };

    case "deposit":
      return {
        "@context": "https://schema.org",
        "@type": "WebPage",
        name: "예치금 관리 - 올려줌 | 충전/환불/이용내역",
        description:
          "예치금 충전, 환불, 이용내역을 편리하게 관리하세요. 실시간 잔액 확인과 상세한 거래 내역을 제공합니다.",
        url: "https://www.olzoom.com/?tab=deposit",
        mainEntity: {
          "@type": "WebApplication",
          name: "예치금 관리 시스템",
          applicationCategory: "FinanceApplication",
          operatingSystem: "Web Browser",
          description: "예치금 충전, 환불, 이용내역 관리 시스템",
        },
      };

    case "settings":
      return {
        "@context": "https://schema.org",
        "@type": "WebPage",
        name: "설정 - 올려줌 | 계정 및 개인정보 관리",
        description:
          "올려줌 계정 설정과 개인정보를 관리하세요. 프로필 수정, 알림 설정, 보안 설정을 변경할 수 있습니다.",
        url: "https://www.olzoom.com/?tab=settings",
        mainEntity: {
          "@type": "WebApplication",
          name: "계정 설정 시스템",
          applicationCategory: "BusinessApplication",
          operatingSystem: "Web Browser",
          description: "계정 및 개인정보 관리 시스템",
        },
      };

    default:
      return baseData;
  }
};

/**
 * URL 정규화 (캐노니컬 URL 생성)
 */
export const normalizeUrl = (path, params = {}) => {
  const baseUrl = "https://www.olzoom.com";
  const url = new URL(path, baseUrl);

  // 파라미터 정렬
  Object.keys(params)
    .sort()
    .forEach((key) => {
      if (params[key]) {
        url.searchParams.set(key, params[key]);
      }
    });

  return url.toString();
};

/**
 * 메타 태그 생성
 */
export const generateMetaTags = (pageInfo) => {
  const { title, description, keywords, ogImage, canonicalUrl, pageType } =
    pageInfo;

  const defaultKeywords =
    "셀프마케팅, 리워드마케팅, 인스타그램마케팅, 유튜브마케팅, 틱톡마케팅, 네이버플레이스, 스마트스토어, 마케팅플랫폼";

  return {
    title: title || "올려줌 - 순위 상위 노출 셀프 마케팅",
    description:
      description ||
      "인스타그램, 유튜브, 틱톡, 스레드, 스마트스토어, 네이버플레이스까지 검색·방문·리뷰·저장·노출을 실제 유저가 직접 실행하는 리워드 트래픽 기반 마케팅 셀프마케팅의 해답, 셀러 대행사 중심 실행 플랫폼 올려줌.",
    keywords: keywords || defaultKeywords,
    ogImage: ogImage || "https://www.olzoom.com/og-image.png",
    canonicalUrl: canonicalUrl || "https://www.olzoom.com/",
    structuredData: generateStructuredData(pageType),
  };
};

/**
 * 페이지 로드 성능 최적화를 위한 리소스 프리로드
 * 빌드된 HTML에서 실제 리소스 경로를 동적으로 찾아서 preload
 */
export const preloadResources = () => {
  try {
    // HTML에 이미 포함된 script와 link 태그에서 실제 리소스 경로 찾기
    const scripts = document.querySelectorAll("script[src]");
    const stylesheets = document.querySelectorAll('link[rel="stylesheet"]');

    // 이미 preload된 리소스 추적 (중복 방지)
    const preloadedHrefs = new Set();
    Array.from(document.querySelectorAll('link[rel="preload"]')).forEach(
      (link) => {
        preloadedHrefs.add(link.href);
      }
    );

    // CSS 파일 preload
    stylesheets.forEach((link) => {
      const href = link.getAttribute("href");
      if (href && !preloadedHrefs.has(href) && href.includes("/static/css/")) {
        const preloadLink = document.createElement("link");
        preloadLink.rel = "preload";
        // 절대 경로 보장 (base href와 상관없이)
        preloadLink.href = href.startsWith("/") ? href : `/${href}`;
        preloadLink.as = "style";
        document.head.insertBefore(preloadLink, link);
        preloadedHrefs.add(href);
      }
    });

    // JS 파일 preload
    scripts.forEach((script) => {
      const src = script.getAttribute("src");
      if (src && !preloadedHrefs.has(src) && src.includes("/static/js/")) {
        const preloadLink = document.createElement("link");
        preloadLink.rel = "preload";
        // 절대 경로 보장 (base href와 상관없이)
        preloadLink.href = src.startsWith("/") ? src : `/${src}`;
        preloadLink.as = "script";
        document.head.appendChild(preloadLink);
        preloadedHrefs.add(src);
      }
    });
  } catch (error) {
    // preload 실패해도 앱 동작에는 영향 없음
    console.warn("Resource preload failed:", error);
  }
};

/**
 * 검색 엔진 크롤링 최적화를 위한 지연 로딩
 */
export const lazyLoadImages = () => {
  const images = document.querySelectorAll("img[data-src]");

  const imageObserver = new IntersectionObserver((entries, observer) => {
    entries.forEach((entry) => {
      if (entry.isIntersecting) {
        const img = entry.target;
        img.src = img.dataset.src;
        img.removeAttribute("data-src");
        observer.unobserve(img);
      }
    });
  });

  images.forEach((img) => imageObserver.observe(img));
};

/**
 * 페이지 로드 완료 후 SEO 이벤트 트리거
 */
export const triggerSEOEvents = () => {
  // 페이지 로드 완료 이벤트
  if (typeof window !== "undefined" && window.gtag) {
    window.gtag("event", "page_view", {
      page_title: document.title,
      page_location: window.location.href,
    });
  }
};
